import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataGridView } from "@neumetric/blueprint-lib/components";
import { Pagination } from "antd";
import { riskActions, getThreatsList } from "../../store";

const PER_PAGE = 7;
const FOOTER_H = 48;

const columns = [
  {
    dataIndex: "threat",
    title: "Threat",
    typeRepresentation: "TEXT",
    width: "40%",
    sortableFlag: true,
    searchableFlag: true,
    showTooltip: true,
  },
  {
    dataIndex: "source",
    title: "Source",
    typeRepresentation: "TEXT",
    width: "20%",
    sortableFlag: true,
    searchableFlag: true,
    showTooltip: true,
  },
  {
    dataIndex: "description",
    title: "Description",
    typeRepresentation: "TEXT",
    width: "40%",
    showTooltip: true,
  },
];

const getPage = (search) => {
  const params = new URLSearchParams(search);
  const p = Number(params.get("page") || 1);
  return Number.isNaN(p) || p < 1 ? 1 : p;
};

export const ThreatsTrackerScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const threatList = useSelector(getThreatsList) || [];
  const totalCount = threatList.length;

  const pageNo = getPage(location.search);
  const maxPage = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  const safePageNo = Math.min(pageNo, maxPage);

  const rootRef = React.useRef(null);
  const [rootHeight, setRootHeight] = React.useState(500);

  React.useEffect(() => {
    dispatch(riskActions.fetchThreats());
  }, [dispatch]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("page")) {
      navigate(`${location.pathname}?page=1`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  //  Make layout stable: fix height based on viewport- made this change as pagination was not coming and if coming tableheight is not coming properly
  React.useLayoutEffect(() => {
    const compute = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      const available = window.innerHeight - top - 12; // bottom gap
      setRootHeight(Math.max(280, available));
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const pagedThreats = React.useMemo(() => {
    const start = (safePageNo - 1) * PER_PAGE;
    return threatList.slice(start, start + PER_PAGE);
  }, [threatList, safePageNo]);

  const handlePageChange = (p) => {
    navigate(`${location.pathname}?page=${p}`);
  };

  const showFooter = totalCount > PER_PAGE;

  return (
    <div
      ref={rootRef}
      className="col w-100 w-1100-px mt-10"
      style={{ height: rootHeight, display: "flex", flexDirection: "column" }}
    >
      {/* Table gets a real height */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <DataGridView
          style={{ height: "100%" }} // IMPORTANT: ensures DataGridView gets full height
          columns={columns}
          dataSource={pagedThreats}
          EmptyComponent="There are no threats right now!"
        />
      </div>

      {/* Footer always visible (no clipping) */}
      <div
        style={{
          height: showFooter ? FOOTER_H : 0,
          display: showFooter ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Pagination
          current={safePageNo}
          pageSize={PER_PAGE}
          total={totalCount}
          showSizeChanger={false}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};
